import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db9m68k6oi3g3tv8v0q0";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setContent("");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      if (typeof value === "string" && value.trim() !== "") body.append(key, value);
    });

    setStatus("submitting");
    setErrorMessage("");

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setContent("");
        form.reset();
      } else {
        setStatus("error");
        setErrorMessage(
          serverMessage && serverMessage !== "OK"
            ? String(serverMessage)
            : "提交没有成功，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setErrorMessage("网络连接异常，内容还没有提交成功，请检查网络后重试。");
    }
  };

  return (
    <div className="rounded-lg border border-background-200/80 bg-background-50 p-5 md:p-7">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-black text-foreground-950">线索提交与反馈</h2>
          <p className="mt-2 text-sm leading-6 text-foreground-500">
            留下你的线索或建议。提交后会由编辑人工查看，我们不会公开你的联系方式。
          </p>
        </div>
        <span className="hidden sm:inline-flex items-center h-6 px-2 rounded bg-primary-100 text-primary-700 text-xs whitespace-nowrap">
          人工查看
        </span>
      </div>

      <form
        id="baoliao-feedback-form"
        data-readdy-form
        action={SUBMIT_ADDR}
        method="post"
        onSubmit={handleSubmit}
        className="mt-5 space-y-4"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="fb-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
              怎么称呼你 <span className="text-primary-500">*</span>
            </label>
            <input
              id="fb-name"
              name="name"
              type="text"
              required
              maxLength={40}
              placeholder="例如：王先生 / 匿名网友"
              className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
          <div>
            <label htmlFor="fb-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
              邮箱 <span className="text-primary-500">*</span>
            </label>
            <input
              id="fb-email"
              name="email"
              type="email"
              required
              maxLength={80}
              placeholder="用于必要时的进一步核实"
              className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="fb-type" className="block text-sm font-medium text-foreground-800 mb-1.5">
              内容类型
            </label>
            <select
              id="fb-type"
              name="type"
              defaultValue="社会爆料线索"
              className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors cursor-pointer"
            >
              <option value="社会爆料线索">社会爆料线索</option>
              <option value="娱乐爆料线索">娱乐爆料线索</option>
              <option value="内容纠错">内容纠错</option>
              <option value="合作与版权">合作与版权</option>
              <option value="其他反馈">其他反馈</option>
            </select>
          </div>
          <div>
            <label htmlFor="fb-contact" className="block text-sm font-medium text-foreground-800 mb-1.5">
              备用联系方式（选填）
            </label>
            <input
              id="fb-contact"
              name="contact"
              type="text"
              maxLength={60}
              placeholder="微信 / 手机号，仅在核实时使用"
              className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </div>
        </div>

        <div>
          <label htmlFor="fb-content" className="block text-sm font-medium text-foreground-800 mb-1.5">
            线索内容 <span className="text-primary-500">*</span>
          </label>
          <textarea
            id="fb-content"
            name="content"
            required
            maxLength={500}
            value={content}
            onChange={(event) => setContent(event.target.value)}
            rows={5}
            placeholder="请描述时间、地点和你能确认的细节，最多 500 字"
            className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm leading-6 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors resize-y"
          />
          <p className="mt-1.5 text-right text-xs text-foreground-400">{content.length} / 500</p>
        </div>

        <input
          className="feedback-extra-field"
          type="text"
          name="website_alt"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          readOnly
        />

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-1">
          <button
            type="submit"
            disabled={status === "submitting"}
            className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
          >
            {status === "submitting" ? (
              <>
                <span className="w-4 h-4 rounded-full border-2 border-background-50/50 border-t-background-50 animate-spin" />
                正在提交…
              </>
            ) : (
              <>
                <i className="ri-send-plane-line" />
                提交线索
              </>
            )}
          </button>
          <p className="text-xs leading-5 text-foreground-400">
            提交即表示你确认提供的信息为本人所知，并同意我们用于核实与报道。
          </p>
        </div>

        {status === "success" && (
          <div className="flex items-start gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-3.5 py-3 text-sm text-secondary-900 animate-fade-in">
            <i className="ri-checkbox-circle-line text-secondary-600 mt-0.5" />
            <span>已收到你的信息，感谢提供线索。编辑会在核实后根据需要与你联系。</span>
          </div>
        )}

        {status === "error" && (
          <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3.5 py-3 text-sm text-primary-800 animate-fade-in">
            <i className="ri-error-warning-line mt-0.5" />
            <span>{errorMessage || "提交失败，请稍后重试。"}</span>
          </div>
        )}
      </form>
    </div>
  );
}